import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import {
  ISnListResponse,
  ISnListQueryParameters,
  ISnFeedQueryParameters,
  transformListResponse,
} from '@lib/api';
import { SnCameraGrid, SnShare } from '@lib/api/entities';
import { ISnCanShare } from '@lib/share';
import { t } from '@lib/translation';
import { SnDialogService } from '@lib/ui/dialog';

@Injectable({ providedIn: 'root' })
export class SnCameraGridsAPI implements ISnCanShare {
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _dialogService: SnDialogService,
  ) {}

  public getCameraGridList(
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnCameraGrid>> {
    params = { is_temporary: false, ...params };
    return this._httpClient
      .get('/api/camera-views/', { params })
      .pipe(
        map((response: any) => transformListResponse(response, (item) => new SnCameraGrid(item))),
      );
  }

  public getCameraGrid(id: string): Observable<SnCameraGrid> {
    return this._httpClient
      .get(`/api/camera-views/${id}/`)
      .pipe(map((response: any) => new SnCameraGrid(response)));
  }

  public createCameraGrid(data: any): Observable<SnCameraGrid> {
    return this._httpClient.post('/api/camera-views/', data).pipe(
      map((response: any) => new SnCameraGrid(response)),
      tap({
        next: () => this._toastrService.success(t('cameras.grid.messages.created')),
        error: () => this._toastrService.error(t('cameras.grid.messages.createError')),
      }),
    );
  }

  public updateCameraGrid(id: string, data: any): Observable<SnCameraGrid> {
    return this._httpClient.patch(`/api/camera-views/${id}/`, data).pipe(
      map((response: any) => new SnCameraGrid(response)),
      tap({
        next: () => this._toastrService.success(t('cameras.grid.messages.updated')),
        error: () => this._toastrService.error(t('cameras.grid.messages.updateError')),
      }),
    );
  }

  public deleteCameraGrid(id: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('cameras.grid.messages.deleteConfirm'),
        confirmButton: {
          text: t('common.delete').toUpperCase(),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.delete(`/api/camera-views/${id}/`)),
        tap({
          next: () => this._toastrService.success(t('cameras.grid.messages.deleted')),
          error: () => this._toastrService.error(t('cameras.grid.messages.deleteError')),
        }),
      );
  }

  public getShareList(
    cameraGridId: string,
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnShare>> {
    return this._httpClient.get(`/api/camera-views/${cameraGridId}/shares/`, { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((share) => new SnShare(share)),
      })),
    );
  }

  public createShare(cameraGridId: string, data: any): Observable<SnShare | SnShare[]> {
    return this._httpClient.post(`/api/camera-views/${cameraGridId}/shares/`, data).pipe(
      map((response: any) => {
        return Array.isArray(response)
          ? response.map((share) => new SnShare(share))
          : new SnShare(response);
      }),
    );
  }

  public updateShare(cameraGridId: string, shareId: string, data: any): Observable<SnShare> {
    return this._httpClient
      .patch(`/api/camera-views/${cameraGridId}/shares/${shareId}/`, data)
      .pipe(map((response: any) => new SnShare(response)));
  }

  public deleteShare(cameraGridId: string, shareId: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('cameras.confirmations.deleteShare'),
        confirmButton: {
          text: t('common.delete').toUpperCase(),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => {
          return this._httpClient.delete(`/api/camera-views/${cameraGridId}/shares/${shareId}/`);
        }),
      );
  }

  public feedCameraGrid(params: ISnFeedQueryParameters): Observable<SnCameraGrid[]> {
    return this._httpClient.get('/api/camera-views/feed/', { params }).pipe(
      map((response: any) => {
        return response.map((view) => new SnCameraGrid(view));
      }),
    );
  }
}
